<template>
  <div class="style-carousel d-flex align-items-center justify-content-center">
    <ScrollButton
      direction="left"
      class="left-button"
      @click="previousItem()"/>
    <ScrollButton
      direction="right"
      class="right-button"
      @click="nextItem()"/>
    <div class="carousel-container d-flex align-items-center">
      <div 
        v-for="(item, idx) in currentItems"
        :key="item.id" 
        v-drag="dragHandler()"
        :drag-options="dragOptions"
        class="style-carousel-item grab-cursor shadow-primary"
        :class="`style-carousel-item-${idx + 1}`">
        <slot
          :item="item"
          :slot-index="idx + 1"/>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { dragDirective } from '@vueuse/gesture'
import ScrollButton from './ScrollButton.vue'

const props = defineProps({
  items: {
    type: Array,
    required: true,
    validator: (value) => value?.length >= 5
  }
})

const vDrag = {
  beforeMount: dragDirective().bind
}

const dragOptions = {
  filterTaps: true,
  swipeDistance: [1, 1],
  preventWindowScrollY: true,
  useTouch: true
}

const transitioning = ref(false)
const currentItems = ref([])
const startIndex = ref(0)
const visibleCount = 5

const updateCurrentItems = () => {
  if (!props.items.length) {
    currentItems.value = []
    return
  }

  currentItems.value = []
  for (let i = 0; i < visibleCount; i++) {
    const idx = (startIndex.value + i) % props.items.length
    currentItems.value.push(props.items[idx])
  }
}

const nextItem = () => {
  if (!props.items.length || transitioning.value) {
    return
  }
  transitioning.value = true
  startIndex.value = (startIndex.value + 1) % props.items.length
  updateCurrentItems()
  setTimeout(() => {
    transitioning.value = false
  }, 500)
}

const previousItem = () => {
  if (!props.items.length || transitioning.value) {
    return
  }
  transitioning.value = true
  startIndex.value = (startIndex.value - 1 + props.items.length) % props.items.length
  updateCurrentItems()
  setTimeout(() => {
    transitioning.value = false
  }, 500)
}

onMounted(() => {
  updateCurrentItems()
})

const dragHandler = () => ({ movement: [x], dragging }) => {
  if (dragging) return
  if (x > 25) {
    previousItem()
  } else if (x < -25) {
    nextItem()
  }
}

watch(
  () => props.items,
  (newItems) => {
    if (!newItems?.length) {
      startIndex.value = 0
      updateCurrentItems()
      return
    }

    if (startIndex.value >= newItems.length) {
      startIndex.value = 0
    }
    updateCurrentItems()
  },
  { deep: true }
)
</script>

<style lang="scss" scoped>
$carousel-height: 216px;
$item-width: 86px;
$transition-duration: 0.5s;
$overlap-width: 50px;
$item-size-1: 1.0;
$item-size-2: 1.25;
$item-size-3: 1.86;
$item-size-4: 1.25;
$item-size-5: 1.0;
$left-position-1: 0px;
$left-position-2: $item-width - $overlap-width; 
$left-position-3: $left-position-2 + ($item-width * $item-size-2 ) - $overlap-width;
$left-position-4: $left-position-3 + ($item-width * $item-size-3) - $overlap-width;
$left-position-5: $left-position-4 + ($item-width * $item-size-4) - $overlap-width;

$positions: (
  1: (left: $left-position-1, size: $item-size-1, opacity: 1.0, z-index: 0),
  2: (left: $left-position-2, size: $item-size-2, opacity: 1.0, z-index: 1),
  3: (left: $left-position-3, size: $item-size-3, opacity: 1.0, z-index: 2),  // center
  4: (left: $left-position-4, size: $item-size-4, opacity: 1.0, z-index: 1),
  5: (left: $left-position-5, size: $item-size-5, opacity: 1.0, z-index: 0),
);

.style-carousel {
  position: relative;
  width: 290px;
  height: $carousel-height;
  margin: 0 auto;
}

.carousel-container {
  position: absolute;
  margin: 0 auto;
  height: $carousel-height;
  width: 347px;
}

.style-carousel-item {
  position: absolute;
  transition: all $transition-duration ease-in-out;
  animation: carousel-item-opacity $transition-duration ease-in-out;
  border-radius: $sequin-border-radius;
  border: 1px solid $pewter;
  aspect-ratio: 3/4;
  height: auto;
  background-color: $white;
  overflow: hidden;
  touch-action: pan-y;
}
@keyframes carousel-item-opacity {
  0% {
      opacity: 1;
  }
  50% {
      opacity: 0;
  }
  100% {
      opacity: 1;
  }
}
@each $pos, $config in $positions {
  .style-carousel-item-#{$pos} {
    left: map-get($config, left);
    width: calc(#{$item-width} * #{map-get($config, size)});
    opacity: map-get($config, opacity);
    z-index: map-get($config, z-index);
  }
}
.buttons-container {
  position: absolute;
  top: calc(50% - 16px);
  z-index: 100;
}
.left-button {
  @extend .buttons-container;
  left: 0px;
}
.right-button {
  @extend .buttons-container;
  right: 0px;
}
</style>